import { cva } from "class-variance-authority";

/**
 * Kanban visual recipe — spec §5.20.
 *
 * Cards are the only raised objects on the board — the columns and the board
 * itself stay on the canvas tier, so nothing nests a raised surface inside
 * another. No brand-tinted drop targets, no `transition-all`, no pill-shaped
 * tags. Columns are 320px — a task card carries a title, a description line, a
 * tag/milestone row and a meta footer, and at 288px the title wrapped to two
 * lines on most real titles.
 */

export const kanbanBoardVariants = cva(
  /* `relative` for the same reason as the column's card scroller: it makes this
     the containing block for the `sr-only` (position: absolute) labels in the
     column headers, so their offsets stay inside this scroller instead of
     inflating the page's scrollable area. */
  "relative flex gap-4 overflow-x-auto p-3 h-full min-h-0",
  {
    variants: {
      size: {
        default: "bg-page",
        compact: "bg-page gap-4 p-3",
        full: "bg-transparent gap-4 p-0",
      },
    },
    defaultVariants: { size: "default" },
  },
);

/**
 * The column is a transparent track: with no inner padding the cards cover its
 * full width, so a tinted surface would only ever show as stripes in the gaps
 * between them. The header and the card stack carry the column's identity; the
 * only time the column paints is the `dragOver` wash.
 */
export const kanbanColumnVariants = cva(
  [
    /* No inner padding: cards run the full column width, edge to edge, the way
       production has them. The column's own `gap-4` on the board is the only
       gutter — anything here would inset every card by that much again. */
    "flex w-80 shrink-0 flex-col gap-2 rounded-xl bg-transparent",
    "transition-[background-color,box-shadow] duration-100 ease-out-fast",
  ].join(" "),
  {
    variants: {
      state: {
        default: "",
        dragOver: "bg-accent-subtle",
        /* The overlay is the dragged column — the source must not also be on
           screen, or the board shows two of it. `opacity-0` rather than
           `hidden`: the node keeps its slot, so the gap the column will drop
           into stays open and dnd-kit can still measure it. */
        dragging: "opacity-0 cursor-grabbing",
      },
    },
    defaultVariants: { state: "default" },
  },
);

export const kanbanColumnHeaderVariants = cva(
  /* Production's shape: with the column transparent, the header is its own
     surface rather than a label floating on the canvas — same tier, radius and
     shadow as a card, and the same `px-2.5` so its title lines up with the
     card titles under it. */
  "flex h-control items-center gap-2 rounded-lg bg-panel dark:bg-elevated px-2.5 shadow-xs",
  {
    variants: {
      draggable: {
        true: "cursor-grab active:cursor-grabbing",
        false: "",
      },
    },
    defaultVariants: { draggable: true },
  },
);

/**
 * Cards carry a fill and nothing else — no hairline. `--color-line` is
 * `transparent` in the light palette on purpose (this system separates with
 * tone, not outlines), so a border here would be dead weight. The tier that
 * makes the card read differs per theme, because the two ramps run opposite
 * ways: light steps DOWN off the white page floor onto the chrome tier
 * (`panel`, the same grey as the sidebar), dark steps UP off the floor onto
 * `elevated`. Either way the column stays transparent and the board area needs
 * no fill of its own. States stay tone-only: nothing here may change the
 * card's box, or every neighbour shifts on hover.
 */
export const kanbanCardVariants = cva(
  [
    "rounded-lg bg-panel dark:bg-elevated px-2.5 py-3 cursor-pointer shadow-xs",
    "transition-[background-color,box-shadow] duration-100 ease-out-fast",
  ].join(" "),
  {
    variants: {
      state: {
        default: "hover:shadow-sm",
        dragging: "opacity-80 cursor-grabbing",
        selected: "bg-accent-subtle",
      },
    },
    defaultVariants: { state: "default" },
  },
);

export const kanbanTagVariants = cva(
  "inline-flex h-5 items-center rounded-xs px-1.5 text-xs font-medium",
  {
    variants: {
      color: {
        default: "bg-hover text-text-secondary",
        blue: "bg-info-subtle text-info",
        green: "bg-success-subtle text-success",
        purple: "bg-hover text-status-review",
        orange: "bg-warning-subtle text-warning",
        red: "bg-danger-subtle text-danger",
      },
    },
    defaultVariants: { color: "default" },
  },
);

export const kanbanEmptyColumnVariants = cva(
  "flex flex-col items-center justify-center px-4 py-8 text-center text-xs text-text-tertiary",
  {
    variants: {
      variant: {
        default: "",
        // Was a dashed outline; a dropzone is a well, not an outline (§1).
        dashed: "mx-1 my-1 rounded-lg bg-sunken",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export const kanbanAddButtonVariants = cva(
  [
    "flex cursor-pointer items-center justify-center rounded-sm text-text-tertiary",
    "transition-[background-color,color] duration-100 ease-out-fast",
    "hover:bg-hover hover:text-text",
    "focus-visible:outline-none focus-fill",
  ].join(" "),
  {
    variants: {
      variant: {
        icon: "size-6",
        full: "h-control w-full text-xs",
      },
    },
    defaultVariants: { variant: "icon" },
  },
);

export const DEFAULT_MENU_ACTIONS = ["view", "edit", "delete"] as const;

export const MENU_ACTION_LABELS: Record<string, string> = {
  view: "View",
  edit: "Edit",
  "copy-branch": "Copy branch name",
  delete: "Delete",
};
